"use client";

import { useEffect, useState } from "react";

interface UseScrollSpyOptions {
  /** IntersectionObserver rootMargin — 활성 판정 기준선(기본: 상단 20% 밴드). */
  rootMargin?: string;
  /** false 면 관찰을 멈추고 active 를 갱신하지 않는다(셸 밖·비활성 컨텍스트). */
  enabled?: boolean;
}

/**
 * 앵커 id 목록 중 현재 뷰포트 상단에 가장 가까운 요소의 id 를 추적하는 scroll-spy 훅 (JCG-2102).
 *
 * 셸 좌측 네비(기출 문제 목록 `ProblemListNav`, 복원 문제 네비 `RestorationNav`)가 공유한다.
 * IntersectionObserver 로 화면에 보이는 앵커 중 top 이 가장 위인 것을 active 로 잡고, 보이는
 * 앵커가 없으면(맨 위/아래) 직전 active 를 유지한다. 반환값은 활성 앵커 id(없으면 null).
 *
 * `anchorIds` 는 **안정 참조**(호출부에서 useMemo)로 전달해야 매 렌더 재관찰을 피한다.
 */
export function useScrollSpy(
  anchorIds: string[],
  options?: UseScrollSpyOptions,
): string | null {
  const { rootMargin = "-20% 0px -70% 0px", enabled = true } = options ?? {};
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const els = anchorIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [anchorIds, rootMargin, enabled]);

  return active;
}
